import React from 'react';
import { Flex, Box, ActionMenu } from '@fullstory/components';
import { EveryoneSegmentId } from '@fullstory/client-api';
import { AccessDeniedMessage, ViewOnlyTooltip } from '../../components';
import { FGACButton, getFGACIconButtonExplorerRoleProps } from '../FGACButton';
import { useIntl } from 'react-intl';
import { noop } from 'lodash';
import { AccessResponse } from '@fullstory/proto/fs/shared/accesscontrol';
import { defaultNoAccess } from '@fullstory/redux/utils/accessControl';
interface SegmentControlsProps {
  segmentId: string;
  canned: boolean;
  onCreateAlertClick: () => void;
  onDuplicateClick: () => void;
  onRenameClick: () => void;
  onArchiveClick: () => void;
  alertsEnabled: boolean;
  additionalMenuItems?: React.ComponentPropsWithoutRef<typeof ActionMenu>['actions'];
  onAdditionalMenuItemSelect?: React.ComponentPropsWithoutRef<typeof ActionMenu>['onSelect'];
  access?: AccessResponse;
}

export const SegmentControls: React.FC<SegmentControlsProps> = ({
  segmentId,
  canned,
  onCreateAlertClick,
  onDuplicateClick,
  onRenameClick,
  onArchiveClick,
  alertsEnabled,
  additionalMenuItems,
  onAdditionalMenuItemSelect,
  access,
}) => {
  const intl = useIntl();
  const { canCreate, canUpdate, canDelete } = access ?? defaultNoAccess;

  return (
    <Flex data-component="SegmentControls" justifyContent="flex-end" gap={8}>
      {alertsEnabled && (
        <FGACButton
          className="createAlert"
          title={intl.formatMessage({ defaultMessage: 'Create Alert', id: 'nQ9q1R' })}
          iconName="bell-plus"
          size="large"
          onClick={onCreateAlertClick}
          disabled={!canCreate}
          tooltip={<AccessDeniedMessage suggestion="none" viewOnly={!canCreate && !canUpdate} />}
          {...getFGACIconButtonExplorerRoleProps(canCreate && !canUpdate)}
        />
      )}
      <Box className="fs_segment_duplicate_button">
        <FGACButton
          title={intl.formatMessage({
            defaultMessage: 'Duplicate',
            id: 'A5JDys',
            description: 'Duplicate button.',
          })}
          iconName="duplicate"
          size="large"
          disabled={segmentId === EveryoneSegmentId || !canCreate}
          onClick={onDuplicateClick}
          tooltip={
            <AccessDeniedMessage
              suggestion="none"
              viewOnly={!(canned && canCreate) && !canUpdate}
            />
          }
          {...getFGACIconButtonExplorerRoleProps(canned && canCreate && !canUpdate)}
        />
      </Box>
      <Box className="fs_segment_rename_button">
        <FGACButton
          title={intl.formatMessage({
            defaultMessage: 'Rename',
            id: 'eem8jt',
            description: 'Rename button',
          })}
          iconName="pencil"
          size="large"
          disabled={canned || !canUpdate}
          onClick={onRenameClick}
          tooltip={
            <AccessDeniedMessage
              suggestion={segmentId === EveryoneSegmentId ? 'save_as' : 'save_as_or_duplicate'}
              isCanned={canned}
              viewOnly={!canCreate && !canUpdate}
            />
          }
          {...getFGACIconButtonExplorerRoleProps(canCreate && !canUpdate)}
        />
      </Box>
      <Box className="fs_segment_delete_button">
        <FGACButton
          testId="archive-button"
          title={intl.formatMessage({
            defaultMessage: 'Archive',
            id: 'jbJGLu',
            description: 'Archive button',
          })}
          iconName="folder"
          size="large"
          disabled={canned || !canDelete}
          onClick={onArchiveClick}
          tooltip={
            <AccessDeniedMessage
              suggestion="none"
              isCanned={canned}
              viewOnly={!canCreate && !canDelete}
            />
          }
          {...getFGACIconButtonExplorerRoleProps(canCreate && !canDelete)}
        />
      </Box>
      {additionalMenuItems && additionalMenuItems.length > 0 && (
        <Box>
          <ViewOnlyTooltip isViewOnly={!canCreate && !canUpdate}>
            <ActionMenu
              actions={additionalMenuItems}
              onSelect={onAdditionalMenuItemSelect ?? noop}
              placement="bottom-end"
              title={intl.formatMessage({ defaultMessage: 'More actions', id: 'S8/4ZI' })}
              disabled={!canCreate && !canUpdate}
            />
          </ViewOnlyTooltip>
        </Box>
      )}
    </Flex>
  );
};
